import React, { useEffect } from 'react';
import { t } from '../../i18n/t';
import type { FeeType, FundFeeLogCheckRow } from '../../types/FundFeeLog';
import './FeeChangeConfirmDialog.css';
import { formatDateOnly } from '../../utils/dateTime';

export type FeeChangeChoice = 'update' | 'add' | 'cancel';

export interface FeeChangeConfirmDialogProps {
  feeType: FeeType;
  row: FundFeeLogCheckRow;
  onChoice: (choice: FeeChangeChoice) => void;
}

// "קיימת כבר שורה ביומן דמי הנאמן/הניהול בטווח של שבוע..." — shown by
// FundRecordPage.saveRecord right before the save request goes out, when
// GET /api/fund-fee-log/check found an existing TrusteeFeeLog/
// ManagementFeeLog row within +-7 days of the fund's coming valuation day.
// Escape / backdrop click / the ביטול button all resolve 'cancel', which
// aborts the save entirely (see FundRecordPage).
export function FeeChangeConfirmDialog({ feeType, row, onChoice }: FeeChangeConfirmDialogProps) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onChoice('cancel');
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onChoice]);

  const handleBackdropMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onChoice('cancel');
  };

  const feeTypeLabel = t(`fundFeeChange.${feeType}`);
  const dateText = formatDateOnly(row.dDate);

  return (
    <div className="fee-change-confirm-dialog__backdrop" onMouseDown={handleBackdropMouseDown}>
      <div
        className="fee-change-confirm-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={t('fundFeeChange.title', { feeType: feeTypeLabel })}
      >
        <div className="fee-change-confirm-dialog__header">
          <h2 className="fee-change-confirm-dialog__title">
            {t('fundFeeChange.title', { feeType: feeTypeLabel })}
          </h2>
        </div>
        <div className="fee-change-confirm-dialog__body">
          <p>
            {t('fundFeeChange.message', {
              feeType: feeTypeLabel,
              date: dateText,
              value: String(row.newPercent),
            })}
          </p>
        </div>
        <div className="fee-change-confirm-dialog__actions">
          <button
            type="button"
            className="fee-change-confirm-dialog__btn fee-change-confirm-dialog__btn--primary"
            onClick={() => onChoice('update')}
          >
            {t('fundFeeChange.updateExisting')}
          </button>
          <button
            type="button"
            className="fee-change-confirm-dialog__btn fee-change-confirm-dialog__btn--secondary"
            onClick={() => onChoice('add')}
          >
            {t('fundFeeChange.addNew')}
          </button>
          <button
            type="button"
            className="fee-change-confirm-dialog__btn fee-change-confirm-dialog__btn--ghost"
            onClick={() => onChoice('cancel')}
          >
            {t('fundFeeChange.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
